/* eslint-disable react/jsx-props-no-spreading */
import { isDurationTooShort } from 'Containers/Teleconsult/Main/helpers';
import useIsHealthSummaryUploading from 'Hooks/files/useIsHealthSummaryUploading';
import { EndReason } from 'Modules/ZoomContainer/constants';
import { func, shape } from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addModal, removeModal } from 'Redux/modules/modal/actions';
import { consultFinish, resetConsultStatus } from 'Redux/modules/webChat/consult/actions';
import { consultFeeResetState, consultFeeUpdate } from 'Redux/modules/webChat/consultFee/actions';
import { getConsultById } from 'Selectors/webChat/consults';
import { Button, Modal, Segment } from 'semantic-ui-react';

import {
  closeAllConsultVideoRoom,
  endConsultVideoRoom,
  getOngoingCall,
} from '../../../../redux/modules/webChat/actions';

import ConsultContent from './ConsultContent';
import useConsultFee from './hooks/useConsultFee';
import useStep, { STEP_THREE } from './hooks/useStep';

export const OnGoingCallContent = () => {
  return (
    <div>
      <div>
        <strong>There is an on-going call between patient and you</strong>
      </div>
      <div>If you still wish to Finish Consult, system will force end the current call.</div>
    </div>
  );
};

const useReset = dispatch => {
  useEffect(
    () => () => {
      dispatch(consultFeeResetState());
      dispatch(resetConsultStatus());
    },
    [dispatch],
  );
};

const ModalConsultChat = props => {
  const dispatch = useDispatch();
  const { step, onBack, onNext } = useStep();
  const { onClose, conversation, doctor } = props;
  useReset(dispatch);
  const {
    latestConsult: { id: consultId },
  } = conversation;
  const { consultFee: consultFeeState } = useConsultFee(consultId);
  const video = useSelector(state => state.webChat.video);
  const videoId = video?.zoomInfo?.id;
  const consult = useSelector(state => state.webChat.consults);
  const consultDetail = useSelector(state => getConsultById(state, consultId));
  const { estimate } = useSelector(state => state.webChat.consultFee);
  const { status: statusConsult, error } = consult;
  const { status, error: feeError } = consultFeeState;

  const { estFee } = useConsultFee(consultId);

  const isFixed = estimate?.fixedFee !== null;

  const updateFeeThenFinish = async (body, ongoingCall) => {
    const ongoingCallArray = ongoingCall?.data ?? [];
    if (ongoingCall) {
      ongoingCallArray?.forEach(e => {
        dispatch(closeAllConsultVideoRoom(e.consultId));
      });
      if (ongoingCallArray?.map(e => e.id)?.includes(videoId)) {
        await dispatch(
          endConsultVideoRoom(videoId, {
            status: 1,
            endReason: EndReason.DoctorEnd,
          }),
        );
      }
    }
    await dispatch(consultFeeUpdate(consultId, body));
    try {
      const extraData = isDurationTooShort(consultDetail?.videoCallDuration || 0)
        ? {
            extraData: {
              byPassVideoDurationRule: true,
            },
          }
        : undefined;

      await dispatch(consultFinish(doctor.id, consultId, extraData));
    } catch (err) {
      if (err?.data?.errorCode === 'EXIST_ONGOING_VIDEO_CALL') {
        dispatch(
          addModal({
            type: 'warningModal',
            id: 1,
            data: {
              content: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>Warning</div>
                  <div style={{ padding: '24px', textAlign: 'center' }}>{err?.data?.message}</div>
                </div>
              ),
              size: 'tiny',
              onOk: () => {
                dispatch(resetConsultStatus());
              },
            },
          }),
        );
      }
    }
  };

  const finishConsult = async body => {
    const ongoingCall = await dispatch(getOngoingCall(consultId));
    const { data: ongoingCallList } = ongoingCall;
    if (ongoingCallList?.length > 0) {
      dispatch(
        addModal({
          type: 'commonModal',
          id: 6,
          data: {
            title: 'On-going Call in Teleconsult',
            content: <OnGoingCallContent />,
            size: 'tiny',
            okButton: (
              <Button
                negative
                onClick={async () => {
                  dispatch(removeModal(6));
                  updateFeeThenFinish(body, ongoingCallList);
                }}>
                Force End Call
              </Button>
            ),
          },
        }),
      );
    } else {
      await updateFeeThenFinish(body);
    }
    onClose();
  };
  const loadingFinish = statusConsult.finishing === 0 || status.updating === 0;
  const isErrorFinish = statusConsult.finishing === 2 || status.updating === 2;
  const renderStepLabel = stepId => {
    const stepLabel = {
      1: 'Step 1: Consult Summary',
      2: 'Step 2: Consultation Fee',
      3: 'Step 3: Review',
    };
    return stepLabel[stepId];
  };

  useEffect(() => {
    estFee();
  }, [estFee]);

  const { isHealthSummaryUploading, showAlert } = useIsHealthSummaryUploading();
  const handleCheckUploadingAndClose = () => {
    if (isHealthSummaryUploading) {
      showAlert();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      open
      onClose={handleCheckUploadingAndClose}
      closeIcon
      closeOnDimmerClick={false}
      size="tiny"
      closeOnEscape={step !== STEP_THREE}
      style={{ width: '450px' }}>
      <Modal.Header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '16px',
          marginRight: '24px',
        }}>
        <div style={{ fontWeight: '600' }}>Finish Consult</div>
        <div style={{ fontWeight: 'normal' }}>{renderStepLabel(step)}</div>
      </Modal.Header>
      <Modal.Content as={Segment} basic style={{ minHeight: 200 }}>
        <ConsultContent
          isFixed={isFixed}
          error={feeError || error}
          consultFee={consultFeeState}
          finishConsult={finishConsult}
          loadingFinish={loadingFinish}
          isErrorFinish={isErrorFinish}
          consult={consultDetail}
          onBack={onBack}
          onNext={onNext}
          step={step}
          {...props}
          onClose={handleCheckUploadingAndClose}
        />
      </Modal.Content>
    </Modal>
  );
};

ModalConsultChat.propTypes = {
  onClose: func.isRequired,
  doctor: shape({}).isRequired,
  conversation: shape({}).isRequired,
};

ModalConsultChat.defaultProps = {};

export default ModalConsultChat;
